import { cn } from "lib/utils";
import React, { useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";

import {
	LookupTotemSchedulingActions,
	TotemScheduling,
} from "store/ducks/totem-scheduling";
import {
	AppointmentSelectionScreen,
	CheckInSuccessScreen,
	ConfirmArrivalScreen,
	IdentifyScreen,
	LookupScreen,
	NoAppointmentScreen,
	WelcomeScreen,
} from "./components";

type TotemScreen =
	| "welcome"
	| "identify"
	| "plate"
	| "cpf"
	| "selection"
	| "confirm"
	| "success"
	| "not-found";

type LookupResponse = {
	data?: TotemScheduling[] | { data?: TotemScheduling[] };
};

const getSchedulesFromResponse = (response: LookupResponse) => {
	const payload = response?.data;
	if (Array.isArray(payload)) return payload;
	if (payload && !Array.isArray(payload) && Array.isArray(payload.data)) {
		return payload.data;
	}
	return [];
};

const Totem: React.FC = () => {
	const [screen, setScreen] = useState<TotemScreen>("welcome");
	const [schedules, setSchedules] = useState<TotemScheduling[]>([]);
	const [selectedSchedule, setSelectedSchedule] =
		useState<TotemScheduling | null>(null);
	const [lastLookupType, setLastLookupType] = useState<"plate" | "cpf">(
		"plate",
	);
	const dispatch = useDispatch();
	const { loading: isSearching, checkingIn: isCheckingIn } = useSelector<
		RootStateOrAny,
		{ loading: boolean; checkingIn: boolean }
	>((state) => state.totemScheduling);

	const handleSearch = (query: { plate?: string; cpf?: string }) => {
		setLastLookupType(query.plate ? "plate" : "cpf");
		dispatch(
			LookupTotemSchedulingActions.request(
				query,
				(response: LookupResponse) => {
					const schedulesFromResponse =
						getSchedulesFromResponse(response);
					setSchedules(schedulesFromResponse);
					setScreen(
						schedulesFromResponse.length
							? "selection"
							: "not-found",
					);
				},
			),
		);
	};

	const handleConfirm = () => {
		if (!selectedSchedule) return;
		dispatch(
			LookupTotemSchedulingActions.checkInRequest(
				{ source: selectedSchedule.source, id: selectedSchedule.id },
				() => setScreen("success"),
			),
		);
	};

	const goHome = () => {
		setSchedules([]);
		setSelectedSchedule(null);
		setScreen("welcome");
	};

	return (
		<div
			className={cn("h-full min-h-screen w-full overflow-auto bg-white")}
		>
			{screen === "welcome" ? (
				<WelcomeScreen onStart={() => setScreen("identify")} />
			) : screen === "identify" ? (
				<IdentifyScreen
					onBack={() => setScreen("welcome")}
					onPlate={() => setScreen("plate")}
					onCpf={() => setScreen("cpf")}
				/>
			) : screen === "plate" || screen === "cpf" ? (
				<LookupScreen
					type={screen}
					onBack={() => setScreen("identify")}
					onSearch={handleSearch}
					loading={isSearching}
				/>
			) : screen === "selection" ? (
				<AppointmentSelectionScreen
					schedules={schedules}
					onSelect={(schedule) => {
						setSelectedSchedule(schedule);
						setScreen("confirm");
					}}
					onBack={() => setScreen("identify")}
				/>
			) : screen === "confirm" && selectedSchedule ? (
				<ConfirmArrivalScreen
					scheduling={selectedSchedule}
					onConfirm={handleConfirm}
					onBack={() => setScreen("selection")}
					loading={isCheckingIn}
				/>
			) : screen === "success" && selectedSchedule ? (
				<CheckInSuccessScreen
					scheduling={selectedSchedule}
					onFinish={goHome}
				/>
			) : (
				<NoAppointmentScreen
					onRetry={() => setScreen(lastLookupType)}
					onHome={goHome}
				/>
			)}
		</div>
	);
};

export default Totem;
